/**
 * Godot 4.6.3's screen-space ambient occlusion, Intel's ASSAO as Godot ports it: the project
 * settings it reads, the buffers it draws into and the constants its passes read. Paths under
 * `renderer_rd/` are in `servers/rendering/renderer_rd/`.
 */
/** `RS::EnvironmentSSAOQuality`, which `RS::EnvironmentSSILQuality` repeats. */
export const SSAO_QUALITY = {
    VERY_LOW: 0,
    LOW: 1,
    MEDIUM: 2,
    HIGH: 3,
    ULTRA: 4,
};
export const SSAO_QUALITIES = Object.values(SSAO_QUALITY);
/** Each setting's `GLOBAL_DEF` (`servers/rendering/rendering_server.cpp:3756-3764`). */
export const SSAO_PROJECT_DEFAULTS = {
    quality: SSAO_QUALITY.MEDIUM,
    isHalfSize: true,
    adaptiveTarget: 0.5,
    blurPasses: 2,
    fadeoutFrom: 50,
    fadeoutTo: 300,
    ssilQuality: SSAO_QUALITY.MEDIUM,
    isSsilHalfSize: true,
};
/** The Environment's `ssao_*` defaults, past `ssao_enabled` (`scene/resources/environment.h:132-139`). */
export const SSAO_ENVIRONMENT_DEFAULTS = {
    radius: 1,
    intensity: 2,
    power: 1.5,
    detail: 0.5,
    horizon: 0.06,
    sharpness: 0.98,
    lightAffect: 0,
    aoChannelAffect: 0,
};
/**
 * The size of each deinterleaved slice, and of the importance map (`ss_effects.cpp:1104-1114`): a
 * half or a quarter of the frame at full size, a quarter or an eighth at half size, rounded up.
 */
export function ssaoBufferSizes(frame, isHalfSize) {
    const shift = isHalfSize ? 2 : 1;
    const divided = (side, by) => Math.trunc((side + by - 1) / by);
    return {
        buffer: { width: divided(frame.width, 1 << shift), height: divided(frame.height, 1 << shift) },
        half: { width: divided(frame.width, 2 << shift), height: divided(frame.height, 2 << shift) },
    };
}
/**
 * The size of `level` of the linear depth's mips: half the frame, rounded up, at level 0, and each
 * level after it half its parent, rounded down (`ss_effects.cpp:510-515`).
 */
export function ssaoDepthLevelSize(frame, level) {
    const levelSide = (side) => Math.max(1, Math.trunc((side + 1) / 2) >> level);
    return { width: levelSide(frame.width), height: levelSide(frame.height) };
}
/** `SSEffects::downsample_depth`'s choice of shader and levels (`ss_effects.cpp:520-547`). */
export function ssaoDepthDownsample(project) {
    const { quality, ssilQuality, isHalfSize: isSsaoHalfSize, isSsilHalfSize } = project;
    let usesMips = quality > SSAO_QUALITY.MEDIUM || ssilQuality > SSAO_QUALITY.MEDIUM;
    let mode = 'plain';
    if (quality === SSAO_QUALITY.VERY_LOW && ssilQuality === SSAO_QUALITY.VERY_LOW)
        mode = 'halfBuffers';
    else if (usesMips)
        mode = 'mips';
    const isHalfSize = isSsaoHalfSize && isSsilHalfSize;
    if (!isHalfSize && isSsaoHalfSize !== isSsilHalfSize) {
        mode = usesMips ? 'fullMips' : 'mips';
        usesMips = true;
    }
    const mipLevels = !usesMips ? 0 : mode === 'fullMips' ? 4 : 3;
    return { mode, isHalfSize, baseLevel: isHalfSize ? 1 : 0, mipLevels, gatherLevel: isSsaoHalfSize ? 1 : 0 };
}
/** The deinterleaved slices the gather and the blur draw: Very Low skips 1 and 2 (`ss_effects.cpp:1069`). */
export function ssaoGatheredSlices(quality) {
    return quality === SSAO_QUALITY.VERY_LOW ? [0, 3] : [0, 1, 2, 3];
}
/**
 * Each blur pass's mode, in order (`ss_effects.cpp:1326-1338`). Very Low blurs once, plainly. Above
 * it the last two passes blur by the edges and any before them blur wide.
 */
export function ssaoBlurModes(quality, blurPasses) {
    if (quality === SSAO_QUALITY.VERY_LOW)
        return ['nonSmart'];
    return Array.from({ length: Math.max(0, blurPasses) }, (_, pass) => pass < blurPasses - 2 ? 'wide' : 'smart');
}
/**
 * Whether the interleave reads the pong buffer, which an odd blur pass writes, rather than the
 * gathered one (`ss_effects.cpp:1405-1411`).
 */
export function ssaoReadsBlurredFromPong(quality, blurPasses) {
    return !(quality > SSAO_QUALITY.VERY_LOW && blurPasses % 2 === 0);
}
/** The interleave back to the full frame (`ss_effects.cpp:1392-1397`). */
export function ssaoInterleaveMode(quality) {
    if (quality === SSAO_QUALITY.VERY_LOW)
        return 'half';
    return quality === SSAO_QUALITY.LOW ? 'nonSmart' : 'smart';
}
/**
 * The 20 rotation-and-scale matrices of the gather's sample disc, four per pass in five sub-passes
 * (`ss_effects.cpp:76-94`), laid out as `SSEffectsGatherConstants.rotation_matrices`.
 */
export const SSAO_ROTATION_MATRICES = rotationMatrices();
function rotationMatrices() {
    const subPassCount = 5;
    const subPassOrder = [0, 1, 4, 3, 2];
    const matrices = [];
    for (let pass = 0; pass < 4; pass++) {
        for (let subPass = 0; subPass < subPassCount; subPass++) {
            const b = subPassOrder[subPass];
            const angle = ((pass + b / subPassCount) * Math.PI) / 2;
            const scale = 1 + (pass - 1.5 + (b - (subPassCount - 1) * 0.5) / subPassCount) * 0.07;
            const cos = Math.fround(Math.cos(angle));
            const sin = Math.fround(Math.sin(angle));
            matrices.push(scale * cos, scale * -sin, -scale * sin, -scale * cos);
        }
    }
    return matrices;
}
const column = (projection, i, j) => projection[i * 4 + j];
function isOrthogonal(projection) {
    return column(projection, 2, 3) === 0;
}
/**
 * `downsample_depth`'s push constants (`ss_effects.cpp:582-600`): the projection with Godot's
 * depth correction, unflipped (`projection.cpp:787-806`), gives the linearisation.
 */
export function ssaoDepthLinearize(projection) {
    if (isOrthogonal(projection)) {
        const m22 = column(projection, 2, 2);
        const m32 = column(projection, 3, 2);
        const m33 = column(projection, 3, 3);
        return { z_near: (m33 + m32) / m22, z_far: (m33 - m32) / -m22, isOrthogonal: true };
    }
    // The corrected row is `-0.5 * z + 0.5 * w`.
    const corrected22 = -0.5 * column(projection, 2, 2) + 0.5 * column(projection, 2, 3);
    const corrected32 = -0.5 * column(projection, 3, 2) + 0.5 * column(projection, 3, 3);
    const mul = -corrected32;
    const add = mul * corrected22 < 0 ? -corrected22 : corrected22;
    return { z_near: mul, z_far: add, isOrthogonal: false };
}
/** The gather's push constants for `frame`, the internal size, seen through `projection`. */
export function ssaoGatherParameters(ssao, project, frame, projection) {
    const { quality, isHalfSize } = project;
    const texelScale = isHalfSize ? 4 : 2;
    const halfScreenPixelSize = [texelScale / frame.width, texelScale / frame.height];
    const tanHalfFovX = 1 / column(projection, 0, 0);
    const tanHalfFovY = 1 / column(projection, 1, 1);
    let radius = ssao.radius;
    let radiusNearLimit = ssao.radius * 1.2;
    if (quality <= SSAO_QUALITY.LOW) {
        radiusNearLimit *= 1.5;
        if (quality === SSAO_QUALITY.VERY_LOW)
            radius *= 0.8;
    }
    radiusNearLimit /= tanHalfFovY;
    const fadeRange = project.fadeoutTo - project.fadeoutFrom;
    const { half } = ssaoBufferSizes(frame, isHalfSize);
    return {
        quality: Math.max(0, quality - 1),
        half_screen_pixel_size: halfScreenPixelSize,
        half_screen_pixel_size_x025: [halfScreenPixelSize[0] * 0.75, halfScreenPixelSize[1] * 0.75],
        size_multiplier: isHalfSize ? 2 : 1,
        detail_intensity: ssao.detail,
        NDC_to_view_mul: [tanHalfFovX * 2, tanHalfFovY * -2],
        NDC_to_view_add: [-tanHalfFovX, tanHalfFovY],
        is_orthogonal: isOrthogonal(projection),
        radius,
        intensity: ssao.intensity,
        shadow_power: ssao.power,
        shadow_clamp: 0.98,
        fade_out_mul: -1 / fadeRange,
        fade_out_add: project.fadeoutFrom / fadeRange + 1,
        horizon_angle_threshold: ssao.horizon,
        inv_radius_near_limit: 1 / radiusNearLimit,
        neg_inv_radius: -1 / radius,
        load_counter_avg_div: 9 / (half.width * half.height * 255),
        adaptive_sample_limit: project.adaptiveTarget,
    };
}
//# sourceMappingURL=screenSpaceAmbientOcclusion.js.map